/**
 * <shipping-estimate>
 *
 * Shows the estimated delivery window for a product variant, sourced from the
 * same rates the checkout shows.
 *
 * Two states:
 *   Cold start — no ZIP entered yet. Shows a generic window (default 2–5 days,
 *     business days unless data-skip-weekends="false"). No API call is made.
 *   Resolved — the buyer entered a ZIP. A throwaway Storefront API cart is built
 *     with the variant plus that address, its delivery options are fetched, and
 *     the window spans the fastest and slowest shipping rate.
 *
 * Where the dates come from
 * -------------------------
 * Two sources, in priority order per option:
 *   1. minEstimatedDeliveryDate / maxEstimatedDeliveryDate — ISO, populated only
 *      when the shop runs Shopify's delivery-promise engine (processing time
 *      enabled, or Shopify Logistics).
 *   2. deliveryPromise — a localised string such as "Tuesday, August 4". This is
 *      what the checkout renders for carrier-calculated rates (FedEx/UPS/USPS),
 *      where the ISO fields come back null. Parsed as a fallback.
 *
 * Carrier rates additionally require `withCarrierRates: true`, which Shopify only
 * permits inside an @defer fragment — hence the two-step create-then-query flow
 * and the multipart/mixed response parsing below. Without it, a shop whose rates
 * are all carrier-calculated returns zero delivery groups.
 */
if (!customElements.get('shipping-estimate')) {
  const DESTINATION_KEY = 'shippingEstimate:destination';
  const CACHE_PREFIX = 'shippingEstimate:cache:';
  // Last successful window, kept so a throttled lookup can show the real date this
  // shopper was quoted before, instead of a generic range or an empty card.
  const LAST_KEY = 'shippingEstimate:last';
  const CACHE_TTL_MS = 30 * 60 * 1000;

  /**
   * Skip the carrier re-check when a variant change only touched an option that
   * cannot affect delivery — colour, by default. Verified on this store: Red,
   * Black and Navy of the same net return byte-identical rates and dates, so the
   * call is pure waste, and carrier lookups are rate-limited.
   *
   * FLIP TO false to re-query on every variant change. Do that if QA finds a
   * product where a supposedly cosmetic option does change the rate (a colour
   * stocked at a different warehouse, say). Nothing else needs changing.
   */
  const SKIP_REFETCH_FOR_IGNORED_OPTIONS = true;

  /**
   * Log every rate the carriers return to the console, as a table, regardless of
   * which display mode is configured. For investigating PDP-vs-checkout gaps: it
   * shows what arrived, how each date was derived, and which rate was chosen.
   *
   * Off for production. Flip to true to investigate a PDP-vs-checkout gap: it
   * dumps every rate, every field, how each date was derived and which rate won.
   */
  const LOG_RATES = false;

  /**
   * How many times a throttled carrier lookup is retried before giving up. The
   * shimmer stays on screen for the whole sequence, so this is also how long the
   * buyer waits: 400ms then 800ms between attempts, about 1.2s in total plus
   * request time. Raise it to be more patient, lower it to fail faster.
   */
  const THROTTLE_RETRIES = 3;

  const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

  const STATE_NAMES = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
    CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'Washington DC',
    FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
    IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
    ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan',
    MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
    NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
    NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
    OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
    RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
    TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
    WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
    PR: 'Puerto Rico', VI: 'U.S. Virgin Islands', GU: 'Guam'
  };

  /**
   * ZIP prefix (first three digits) ranges to state, as [low, high, code].
   *
   * Needed because Shopify will not resolve a state from a bare ZIP: the
   * delivery group's address comes back with province null, and STRICT address
   * validation demands a name, phone, city and street the buyer has not given.
   */
  const ZIP_RANGES = [
    [5, 5, 'NY'], [6, 7, 'PR'], [8, 8, 'VI'], [9, 9, 'PR'],
    [10, 27, 'MA'], [28, 29, 'RI'], [30, 38, 'NH'],
    [39, 49, 'ME'], [50, 59, 'VT'], [60, 69, 'CT'], [70, 89, 'NJ'],
    [100, 149, 'NY'], [150, 196, 'PA'], [197, 199, 'DE'], [200, 205, 'DC'],
    [206, 219, 'MD'], [220, 246, 'VA'], [247, 268, 'WV'], [270, 289, 'NC'],
    [290, 299, 'SC'], [300, 319, 'GA'], [320, 349, 'FL'], [350, 369, 'AL'],
    [370, 385, 'TN'], [386, 397, 'MS'], [398, 399, 'GA'], [400, 427, 'KY'],
    [430, 459, 'OH'], [460, 479, 'IN'], [480, 499, 'MI'], [500, 528, 'IA'],
    [530, 549, 'WI'], [550, 567, 'MN'], [570, 577, 'SD'], [580, 588, 'ND'],
    [590, 599, 'MT'], [600, 629, 'IL'], [630, 658, 'MO'], [660, 679, 'KS'],
    [680, 693, 'NE'], [700, 714, 'LA'], [716, 729, 'AR'],
    // 733xx is Austin, Texas — a carve-out inside the Oklahoma range.
    [730, 732, 'OK'], [733, 733, 'TX'], [734, 749, 'OK'],
    [750, 799, 'TX'], [800, 816, 'CO'], [820, 831, 'WY'], [832, 838, 'ID'],
    [840, 847, 'UT'], [850, 865, 'AZ'], [870, 884, 'NM'], [889, 898, 'NV'],
    [900, 961, 'CA'], [967, 968, 'HI'], [969, 969, 'GU'], [970, 979, 'OR'],
    [980, 994, 'WA'], [995, 999, 'AK']
  ];

  // Every field on CartDeliveryOption, so the console dump is complete.
  const OPTION_FIELDS = `
    handle
    title
    code
    description
    deliveryMethodType
    deliveryPromise
    estimatedCost { amount currencyCode }
    minEstimatedDeliveryDate
    maxEstimatedDeliveryDate
  `;

  const CREATE_CART = (withContext) => `
    mutation ShippingEstimateCart(
      $lines: [CartLineInput!]!
      $delivery: CartDeliveryInput!
      ${withContext ? '$country: CountryCode!' : ''}
    ) ${withContext ? '@inContext(country: $country)' : ''} {
      cartCreate(input: { lines: $lines, delivery: $delivery }) {
        userErrors { field message }
        cart { id }
      }
    }
  `;

  const DELIVERY_GROUPS = (withContext) => `
    query ShippingEstimateGroups($id: ID!${withContext ? ', $country: CountryCode!' : ''})
    ${withContext ? '@inContext(country: $country)' : ''} {
      cart(id: $id) {
        id
        ... @defer {
          deliveryGroups(first: 4, withCarrierRates: true) {
            edges {
              node {
                id
                groupType
                deliveryAddress {
                  address1 address2 city company country countryCodeV2
                  firstName lastName latitude longitude name phone
                  province provinceCode zip
                }
                selectedDeliveryOption { ${OPTION_FIELDS} }
                deliveryOptions { ${OPTION_FIELDS} }
              }
            }
          }
        }
      }
    }
  `;

  /**
   * Announces any successful cart mutation as `se:cart-mutated`.
   *
   * The theme's own on:cart:add / on:line-item:change cover its own forms and the
   * cart drawer, but upsell apps (Aftersell, Upnova, quick-add widgets) post to
   * /cart/add directly and fire nothing. Watching the network layer catches all of
   * them. Both wrappers pass through untouched; they only observe.
   */
  const watchCartRequests = () => {
    if (window.__shippingEstimateCartWatch) return;
    window.__shippingEstimateCartWatch = true;

    const announce = () => document.dispatchEvent(new CustomEvent('se:cart-mutated'));
    const isMutation = (url, method) => (
      /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/.test(String(url || ''))
      && String(method || 'GET').toUpperCase() === 'POST'
    );

    const originalFetch = window.fetch;
    window.fetch = function seCartFetch(input, init) {
      const url = typeof input === 'string' ? input : input?.url;
      const method = init?.method || (typeof input === 'object' ? input?.method : 'GET');
      const promise = originalFetch.apply(this, arguments);
      if (isMutation(url, method)) {
        promise.then((res) => { if (res?.ok) announce(); }).catch(() => {});
      }
      return promise;
    };

    const originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function seCartOpen(method, url) {
      this.__seCartMutation = isMutation(url, method);
      return originalOpen.apply(this, arguments);
    };
    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function seCartSend() {
      if (this.__seCartMutation) {
        this.addEventListener('load', () => {
          if (this.status >= 200 && this.status < 300) announce();
        });
      }
      return originalSend.apply(this, arguments);
    };
  };

  class ShippingEstimate extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.token = this.dataset.token;
      this.apiVersion = this.dataset.apiVersion || 'unstable';
      // Always domain-root: the Storefront API is not served under a locale prefix,
      // so Shopify.routes.root must not be used to build this.
      this.endpoint = `/api/${this.apiVersion}/graphql.json`;
      this.variantId = this.dataset.variantId;
      this.quantity = parseInt(this.dataset.quantity, 10) || 1;
      this.locale = this.dataset.locale || document.documentElement.lang || 'en';
      this.syncQuantity = this.dataset.syncQuantity === 'true';
      this.separator = this.dataset.rangeSeparator || ' - ';
      this.country = this.dataset.country || 'US';
      this.countryName = this.dataset.countryName || this.country;
      this.fallbackMin = parseInt(this.dataset.fallbackMinDays, 10) || 2;
      this.fallbackMax = parseInt(this.dataset.fallbackMaxDays, 10) || 5;
      this.skipWeekends = this.dataset.skipWeekends !== 'false';

      this.coldStart = this.dataset.coldStart || 'prompt';
      this.dateDisplay = this.dataset.dateDisplay || 'single';
      this.cartScope = this.dataset.cartScope || 'cart';
      this.available = this.dataset.available !== 'false';
      this.showOnBackorder = this.dataset.showOnBackorder === 'true';
      // Liquid emits these because inventory_quantity is absent from the variant
      // JSON, so backorder state cannot be derived from the dispatched variant.
      this.backorderIds = new Set(
        (this.dataset.backorderIds || '').split(',').map((id) => id.trim()).filter(Boolean)
      );
      this.useCartQuantity = this.dataset.useCartQuantity !== 'false';
      this.optionNames = (this.dataset.optionNames || '').split('|')
        .map((name) => name.trim().toLowerCase());
      this.optionValues = (this.dataset.optionValues || '').split('|');
      this.ignoredOptions = (this.dataset.ignoreOptions || 'color,colour').split(',')
        .map((name) => name.trim().toLowerCase())
        .filter(Boolean);
      this.singleDateSource = this.dataset.singleDateSource || 'cheapest';

      this.els = {
        result: this.querySelector('[data-se-result]'),
        prompt: this.querySelector('[data-se-prompt]'),
        range: this.querySelector('[data-se-range]'),
        method: this.querySelector('[data-se-method]'),
        cost: this.querySelector('[data-se-cost]'),
        destination: this.querySelector('[data-se-destination]'),
        destinationText: this.querySelector('[data-se-destination-text]'),
        trigger: this.querySelector('[data-se-trigger]'),
        modal: this.querySelector('[data-se-modal]'),
        closeBtn: this.querySelector('[data-se-close]'),
        zipInput: this.querySelector('[data-se-zip]'),
        zipSubmit: this.querySelector('[data-se-submit]'),
        free: this.querySelector('[data-se-free]'),
        error: this.querySelector('[data-se-error]')
      };

      this.zip = this.readZip();
      this.region = '';

      this.bindEvents();
      // A product that cannot be bought gets no delivery promise.
      this.suppress('out-of-stock', !this.available);
      this.suppress('backorder', this.isBackordered(this.variantId));
      this.estimate();
    }

    disconnectedCallback() {
      document.removeEventListener('on:variant:change', this.onVariantChange);
      if (this.onCartChange) {
        document.removeEventListener('on:line-item:change', this.onCartChange);
        document.removeEventListener('on:cart:add', this.onCartChange);
        document.removeEventListener('se:cart-mutated', this.onCartChange);
      }
      this.paintWatcher?.disconnect();
      clearTimeout(this.cartTimer);
      clearTimeout(this.quantityTimer);
    }

    bindEvents() {
      this.onVariantChange = (evt) => {
        const id = evt.detail?.variant?.id;
        if (!id) return;

        // Availability is per variant, so this is re-checked on every switch even
        // when the id has not changed.
        const available = evt.detail.variant.available !== false;
        if (available !== this.available) {
          this.available = available;
          this.suppress('out-of-stock', !available);
        }
        // Re-evaluated per variant: one colour can be backordered while another
        // is in stock.
        this.suppress('backorder', this.isBackordered(id));

        if (String(id) === String(this.variantId)) return;

        const values = evt.detail?.variant?.options;
        const cosmeticOnly = SKIP_REFETCH_FOR_IGNORED_OPTIONS
          && this.changedOnlyIgnoredOptions(values);

        this.variantId = String(id);
        if (Array.isArray(values)) this.optionValues = values.slice();

        // Colour-only switch: the rates cannot differ, so keep what is on screen
        // rather than spending a rate-limited carrier call.
        if (cosmeticOnly) return;

        // Silent: keep the current dates visible while the new variant is checked.
        this.estimate(true);
      };
      document.addEventListener('on:variant:change', this.onVariantChange);

      if (this.cartScope === 'cart') {
        this.onCartChange = () => {
          clearTimeout(this.cartTimer);
          // Deliberately NOT silent: the buyer just changed their cart, so the
          // shimmer confirms the estimate is being recalculated for it. A colour
          // switch stays silent because its answer cannot change.
          this.cartTimer = setTimeout(() => this.estimate(false, true), 500);
        };

        // Cart awareness is attached only once the block is actually on screen, so
        // an A/B control that never paints leaves the page exactly as it was — no
        // fetch/XHR wrapper, no listeners, no carrier calls.
        //
        // Gated on real visibility rather than the --hidden class, because the
        // variation reveals the block with CSS and keeps that class. A
        // ResizeObserver covers the reveal landing after this code runs.
        const attachCartWatch = () => {
          if (this.cartWatchAttached) return;
          this.cartWatchAttached = true;
          // Both bubble to document: on:line-item:change covers quantity edits and
          // removals from the cart drawer, on:cart:add covers a brand new line.
          document.addEventListener('on:line-item:change', this.onCartChange);
          document.addEventListener('on:cart:add', this.onCartChange);
          // Backstop for apps that bypass the theme's events entirely.
          watchCartRequests();
          document.addEventListener('se:cart-mutated', this.onCartChange);
        };

        if (this.isPainted()) {
          attachCartWatch();
        } else {
          this.paintWatcher = new ResizeObserver(() => {
            if (!this.isPainted()) return;
            this.paintWatcher.disconnect();
            this.paintWatcher = null;
            attachCartWatch();
            // Pick up anything that changed while we were not listening.
            this.estimate(true);
          });
          this.paintWatcher.observe(this);
        }
      }

      this.els.trigger?.addEventListener('click', () => this.openModal());
      this.els.prompt?.addEventListener('click', () => this.openModal());
      this.els.closeBtn?.addEventListener('click', () => this.closeModal());

      // Clicking the backdrop resolves to the dialog element itself.
      this.els.modal?.addEventListener('click', (evt) => {
        if (evt.target === this.els.modal) this.closeModal();
      });

      this.els.zipSubmit?.addEventListener('click', () => this.applyZip());

      this.els.zipInput?.addEventListener('keydown', (evt) => {
        if (evt.key !== 'Enter') return;
        // Stops the surrounding product form from submitting to /cart/add.
        evt.preventDefault();
        this.applyZip();
      });

      if (this.syncQuantity) {
        const input = this.closest('form')?.querySelector('input[name="quantity"]')
          || document.querySelector('product-form input[name="quantity"]');
        input?.addEventListener('change', () => {
          const next = parseInt(input.value, 10) || 1;
          if (next === this.quantity) return;
          this.quantity = next;
          clearTimeout(this.quantityTimer);
          this.quantityTimer = setTimeout(() => this.estimate(), 400);
        });
      }
    }

    /**
     * @param {Array<string>} values - The new variant's option values, in order.
     * @returns {boolean} True when every option that changed is one listed as
     *   irrelevant to delivery. False if anything else moved, or if the option
     *   data is missing — an unknown change is treated as significant.
     */
    changedOnlyIgnoredOptions(values) {
      if (!Array.isArray(values) || !this.optionNames.length) return false;
      if (values.length !== this.optionValues.length) return false;

      const changed = values
        .map((value, index) => (value === this.optionValues[index] ? -1 : index))
        .filter((index) => index >= 0);

      if (!changed.length) return false;
      return changed.every((index) => this.ignoredOptions.includes(this.optionNames[index]));
    }

    /* ------------------------------------------------------------------ *
     * ZIP + modal
     * ------------------------------------------------------------------ */

    /**
     * The buyer always enters their own ZIP — nothing is pre-filled from the
     * customer record. Only a ZIP they typed before is restored.
     * @returns {string} Saved ZIP, or an empty string on a cold start.
     */
    readZip() {
      try {
        const saved = JSON.parse(localStorage.getItem(DESTINATION_KEY) || 'null');
        return saved?.country === this.country ? (saved.zip || '') : '';
      } catch (error) {
        return '';
      }
    }

    writeZip() {
      try {
        localStorage.setItem(
          DESTINATION_KEY,
          JSON.stringify({ country: this.country, zip: this.zip })
        );
      } catch (error) {
        // Storage is full or blocked; the estimate still works for this page view.
      }
    }

    openModal() {
      if (!this.els.modal) return;
      if (this.els.zipInput) this.els.zipInput.value = this.zip;
      this.showError('');
      this.els.modal.showModal();
      this.els.zipInput?.focus();
      this.els.zipInput?.select();
    }

    closeModal() {
      this.els.modal?.close();
    }

    /**
     * @param {string} zip - Candidate ZIP.
     * @returns {boolean} Whether it is a well-formed US ZIP or ZIP+4.
     */
    static isValidZip(zip) {
      return /^\d{5}(-\d{4})?$/.test(zip);
    }

    /**
     * Validates, then confirms the ZIP actually produces rates before committing.
     *
     * Shopify accepts anything here — "ABCDE", "00000" and a real Alaska ZIP the
     * store does not serve all return a cart with zero rates and no error. So the
     * format is checked locally, and a well-formed ZIP is only saved and the modal
     * only closed once rates come back. Otherwise the previous destination is
     * restored and the reason is shown in the modal.
     */
    async applyZip() {
      const zip = (this.els.zipInput?.value || '').trim().toUpperCase();
      this.showError('');

      if (!ShippingEstimate.isValidZip(zip)) {
        this.showError(this.dataset.errorFormat || 'Enter a valid 5-digit ZIP code.');
        this.els.zipInput?.focus();
        return;
      }

      if (zip === this.zip && this.dataset.state === 'ready') {
        this.closeModal();
        return;
      }

      // Close straight away rather than holding the buyer while the carriers are
      // queried; the card shimmers in place, and the modal only comes back if the
      // ZIP turns out to be unusable.
      const previousZip = this.zip;
      this.zip = zip;
      this.closeModal();
      this.showLoading();

      // Must resolve the basket first: load() quotes this.cartLines, and without
      // this the first lookup after entering a ZIP quoted the product alone and
      // cached it under a key with no cart signature.
      await this.resolveLines();
      const outcome = await this.load();
      if (outcome === 'ok') {
        this.writeZip();
        return;
      }

      // Throttled or offline: the ZIP is probably fine, so keep it rather than
      // blaming the buyer. The generic window stands in until the next attempt.
      if (outcome === 'incompatible') {
        this.renderDisabled();
        return;
      }

      if (outcome === 'unavailable') {
        this.writeZip();
        this.renderUnavailable();
        return;
      }

      // Cannot tell an unknown ZIP from one the store will not ship to — the API
      // reports both as simply no rates — so the message covers both.
      this.zip = previousZip;
      this.estimate();
      this.openModal();
      // Put back what they typed, so they can correct it rather than retype it.
      if (this.els.zipInput) this.els.zipInput.value = zip;
      this.showError(this.dataset.errorNoRates
        || 'No delivery options for that ZIP. Please check it and try again.');
      this.els.zipInput?.focus();
      this.els.zipInput?.select();
    }

    /**
     * @param {string} message - Error text, or '' to clear.
     */
    showError(message) {
      if (!this.els.error) return;
      this.els.error.textContent = message;
      this.els.error.hidden = !message;
      this.els.zipInput?.setAttribute('aria-invalid', message ? 'true' : 'false');
    }


    /**
     * Works out which lines the carriers should price.
     *
     * Rates depend on the whole order — weight, and this store's free-shipping
     * threshold — so the answer differs by scope. 'product' quotes this item alone,
     * which is what a shopper with an empty cart will see at checkout. 'cart' adds
     * the item to whatever is already in the cart, which is what a shopper who has
     * been adding things will see.
     *
     * Also records a signature of those lines, so the cache cannot serve a price
     * that was quoted for a different basket.
     */
    async resolveLines() {
      const gid = (id) => `gid://shopify/ProductVariant/${id}`;
      const own = { merchandiseId: gid(this.variantId), quantity: this.quantity };

      if (this.cartScope !== 'cart') {
        this.cartSignature = 'product';
        this.cartLines = [own];
        return;
      }

      let items = [];
      try {
        // cart.js IS served under the locale prefix, unlike the Storefront API.
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (response.ok) items = (await response.json()).items || [];
      } catch (error) {
        // Unreadable cart: fall back to quoting this item alone rather than failing.
      }

      const quantities = new Map();
      items.forEach((item) => {
        const key = String(item.id);
        quantities.set(key, (quantities.get(key) || 0) + item.quantity);
      });
      const viewed = String(this.variantId);

      if (!quantities.has(viewed)) {
        // Not in the cart yet, so the estimate answers "if I buy this".
        quantities.set(viewed, this.quantity);
      } else if (!this.useCartQuantity) {
        // Opted out: assume they will raise the line to the PDP quantity.
        quantities.set(viewed, Math.max(quantities.get(viewed), this.quantity));
      }
      // Otherwise the cart's own quantity stands: someone viewing a product they
      // have already added is most likely to open the cart and check out, so the
      // cart as it stands IS the order being priced.

      const entries = [...quantities.entries()].sort((a, b) => a[0].localeCompare(b[0]));
      this.cartSignature = entries.map(([id, qty]) => `${id}:${qty}`).join(',');
      this.cartLines = entries.map(([id, qty]) => ({ merchandiseId: gid(id), quantity: qty }));
    }

    /* ------------------------------------------------------------------ *
     * Estimating
     * ------------------------------------------------------------------ */

    /**
     * Nothing is fetched until a ZIP is known, so a first-time visitor costs no
     * API calls at all. Once one is known the fetch runs straight away rather than
     * waiting for the block to scroll into view: a visibility gate would leave the
     * widget shimmering forever for anyone who never scrolls to it, and it would
     * make a CSS-hidden A/B control behave differently from the variation.
     */
    /**
     * @param {boolean} silent - Revalidate without shimmering, keeping the current
     *   window on screen until the new one arrives. Used for variant changes:
     *   swapping colour changes the merchandise ID so the rates must be re-checked,
     *   but colour does not change weight, so the answer is almost always the same
     *   and a flash of skeleton would be noise. Size variants can genuinely differ,
     *   which is why the call still happens.
     */
    async estimate(silent, cartChanged) {
      // Suppressed for any reason means no date should be shown, so none is fetched.
      if (!this.available || this.suppressReasons?.size) {
        this.setState('idle');
        return;
      }

      if (this.incompatible) {
        this.setState('idle');
        return;
      }

      if (!this.zip || !this.token) {
        this.renderColdStart();
        return;
      }

      // Before the cache is consulted: the basket is part of the cache identity.
      await this.resolveLines();

      const cached = this.readCache();
      if (cached) {
        // A cached basket renders instantly, which after a cart change looks like
        // nothing happened. Hold a brief shimmer so the recalculation is visible.
        if (cartChanged) {
          this.showLoading();
          await new Promise((resolve) => { setTimeout(resolve, 350); });
        }
        this.region = cached.region || '';
        this.renderWindow(
          new Date(cached.min),
          cached.max ? new Date(cached.max) : null,
          cached.option,
          cached.hasFree
        );
        return;
      }

      if (!silent || this.dataset.state !== 'ready') this.showLoading();
      this.load().then((outcome) => {
        if (outcome === 'incompatible') this.renderDisabled();
        else if (outcome === 'unavailable') this.renderUnavailable();
        else if (outcome !== 'ok') {
          // 'empty' — rates returned without dates. Same preference as a throttle.
          if (!this.renderLastKnown()) this.renderColdStart();
        }
      });
    }

    /**
     * @returns {Promise<string>} One of:
     *   'ok'        — a real window was rendered.
     *   'empty'     — the carriers quoted nothing: an unknown ZIP, or a
     *                 destination the store does not ship to. Safe to reject.
     *   'unavailable' — throttled, offline or errored. The ZIP may be perfectly
     *                 good, so it must NOT be rejected on this.
     */
    async load() {
      this.requestId = (this.requestId || 0) + 1;
      const requestId = this.requestId;
      this.failureReason = '';

      try {
        let cartId = await this.createCart(true);
        // Falls over when the destination country has no active market. Retrying
        // without the market directive still gives the shop-default rates.
        if (!cartId) cartId = await this.createCart(false);
        if (requestId !== this.requestId) return 'unavailable';
        if (!cartId) throw new Error('cart creation failed');

        // Carrier throttling is usually brief. The shimmer is deliberately held for
        // the whole sequence — nothing is rendered between attempts — so the buyer
        // sees "working", never a placeholder date that is then replaced. Only if
        // every attempt is throttled does this give up, and the caller then shows
        // no date rather than inventing one.
        //
        // Exponential backoff across THROTTLE_RETRIES attempts. Nothing is
        // rendered between them, so there is no flash mid-sequence.
        let groups = [];
        for (let attempt = 0; attempt < THROTTLE_RETRIES; attempt += 1) {
          if (attempt) {
            await new Promise((resolve) => { setTimeout(resolve, 400 * (2 ** (attempt - 1))); });
          }
          const fetched = await this.fetchGroups(cartId);
          if (requestId !== this.requestId) return 'unavailable';
          if (!fetched.throttled) { groups = fetched.groups; break; }
          if (attempt === THROTTLE_RETRIES - 1) {
            this.failureReason = 'carrier rates throttled';
            return 'unavailable';
          }
        }

        const result = ShippingEstimate.computeWindow(
          groups, ShippingEstimate.today(), this.dateDisplay, this.singleDateSource
        );

        if (LOG_RATES) this.logRates(groups, result);
        this.region = result.region;

        if (!result.min) return 'empty';

        const payload = {
          min: result.min.toISOString(),
          // Null in single-date mode, where there is only one bound.
          max: result.max ? result.max.toISOString() : null,
          region: result.region,
          option: result.option,
          hasFree: result.hasFree
        };
        this.writeCache(payload);
        this.writeLastKnown(payload);
        this.renderWindow(result.min, result.max, result.option, result.hasFree);
        return 'ok';
      } catch (error) {
        if (requestId !== this.requestId) return 'unavailable';
        console.warn('[shipping-estimate]', error); // eslint-disable-line no-console
        if (/doesn't exist on type/.test(error.message)) return 'incompatible';
        this.failureReason = 'carrier rates unavailable';
        return 'unavailable';
      }
    }

    /**
     * @param {boolean} withContext - Whether to scope the query to the buyer's market.
     * @returns {Promise<string|null>} The new cart's ID.
     */
    async createCart(withContext) {
      const variables = {
        lines: this.cartLines || [{
          merchandiseId: `gid://shopify/ProductVariant/${this.variantId}`,
          quantity: this.quantity
        }],
        delivery: {
          addresses: [{
            // provinceCode matters: checkout will not rate an address without a
            // state, and the API does not refuse — it silently rates a
            // province: null address a day more conservatively. Verified on 90011,
            // where adding it moved every rate one day earlier and made the API
            // match checkout exactly. Derived from the ZIP locally, since Shopify
            // will not resolve a state from a bare ZIP.
            address: {
              deliveryAddress: {
                countryCode: this.country,
                zip: this.zip,
                provinceCode: ShippingEstimate.stateFromZip(this.zip) || undefined
              }
            },
            selected: true,
            // Never persist a browsing-time guess against the buyer.
            oneTimeUse: true
          }]
        }
      };
      if (withContext) variables.country = this.country;

      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.token
        },
        body: JSON.stringify({ query: CREATE_CART(withContext), variables })
      });

      if (!response.ok) throw new Error(`Storefront API responded ${response.status}`);
      const data = await response.json();
      if (data.errors) return null;

      const payload = data.data?.cartCreate;
      if (payload?.userErrors?.length) {
        throw new Error(payload.userErrors.map((e) => e.message).join('; '));
      }
      return payload?.cart?.id || null;
    }

    /**
     * Fetches delivery groups including carrier-calculated rates. The @defer
     * fragment makes Shopify stream the response as multipart/mixed, so the body
     * arrives as an initial payload plus one incremental patch.
     *
     * Shopify rate-limits carrier lookups and reports it inside the deferred
     * chunk as THROTTLED with a null payload — not as an HTTP error. That must not
     * be mistaken for "this address has no delivery options", which is what the
     * buyer would otherwise be told about a perfectly valid ZIP.
     *
     * @param {string} cartId - Cart to read.
     * @returns {Promise<{groups: Array, throttled: boolean}>} Groups, or a throttle flag.
     */
    async fetchGroups(cartId) {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.token,
          Accept: 'multipart/mixed'
        },
        body: JSON.stringify({
          query: DELIVERY_GROUPS(true),
          variables: { id: cartId, country: this.country }
        })
      });

      if (!response.ok) throw new Error(`Storefront API responded ${response.status}`);

      const contentType = response.headers.get('content-type') || '';
      const text = await response.text();

      const isThrottled = (errors) => (errors || []).some((err) => (
        err?.extensions?.code === 'THROTTLED' || /throttl|temporarily unavailable/i.test(err?.message || '')
      ));

      // Shopify may answer a deferred query as plain JSON if nothing was streamed.
      if (!contentType.includes('multipart/mixed')) {
        const data = JSON.parse(text);
        if (isThrottled(data.errors)) return { groups: [], throttled: true };
        // A schema error arrives as HTTP 200 with `errors` and no data — that is
        // what happens the day these fields move or are withdrawn from `unstable`.
        // It must surface as a failure, never as "this ZIP has no delivery
        // options", which would blame the buyer for our own breakage.
        if (data.errors?.length) {
          throw new Error(data.errors.map((err) => err.message).join('; '));
        }
        return {
          groups: (data.data?.cart?.deliveryGroups?.edges || []).map((edge) => edge.node),
          throttled: false
        };
      }

      const boundary = (contentType.match(/boundary=([^;]+)/) || [])[1] || 'graphql';
      const edges = [];
      let throttled = false;
      const failures = [];

      const note = (errors) => {
        if (!errors?.length) return;
        if (isThrottled(errors)) throttled = true;
        else failures.push(...errors.map((err) => err.message));
      };

      ShippingEstimate.parseMultipart(text, boundary).forEach((chunk) => {
        note(chunk.errors);
        const direct = chunk.data?.cart?.deliveryGroups?.edges;
        if (direct) edges.push(...direct);
        (chunk.incremental || []).forEach((patch) => {
          note(patch.errors);
          const patched = patch.data?.deliveryGroups?.edges;
          if (patched) edges.push(...patched);
        });
      });

      // Same reasoning as above: a real error is not an empty result.
      if (!edges.length && failures.length) throw new Error(failures.join('; '));

      return { groups: edges.map((edge) => edge.node), throttled: throttled && !edges.length };
    }

    /**
     * @param {string} text - Raw multipart/mixed body.
     * @param {string} boundary - Boundary token from the content-type header.
     * @returns {Array<object>} Each part's parsed JSON payload.
     */
    static parseMultipart(text, boundary) {
      return text
        .split(`--${boundary}`)
        .map((part) => {
          const start = part.indexOf('\r\n\r\n');
          if (start === -1) return null;
          const body = part.slice(start + 4).trim();
          if (!body || body === '--') return null;
          try {
            return JSON.parse(body);
          } catch (error) {
            return null;
          }
        })
        .filter(Boolean);
    }

    /* ------------------------------------------------------------------ *
     * Working out the window
     * ------------------------------------------------------------------ */

    /**
     * Spans the fastest and slowest shipping rate, which is what the buyer is
     * really being told: the earliest it could land and the latest it should.
     *
     * @param {Array} groups - Delivery group nodes.
     * @param {Date} today - Today, at UTC midnight.
     * @returns {{min: Date|null, max: Date|null, region: string, option: object|null}}
     */
    static computeWindow(groups, today, mode, source) {
      const group = groups.find((g) => g.groupType === 'ONE_TIME_PURCHASE') || groups[0];
      if (!group) return { min: null, max: null, region: '', option: null };

      const address = group.deliveryAddress || {};
      // province comes back as a code ("CA") when it is populated at all.
      const region = STATE_NAMES[address.provinceCode || address.province]
        || address.province
        || address.city
        || '';

      // Each rate reduced to its own dates, so a single rate can be singled out.
      const priced = (group.deliveryOptions || [])
        .filter((o) => o.deliveryMethodType === 'SHIPPING')
        .map((option) => {
          // ISO fields win when the shop runs Shopify's delivery promise; carrier
          // rates leave them null and put the date in deliveryPromise instead.
          const dates = [];
          if (option.minEstimatedDeliveryDate) {
            dates.push(ShippingEstimate.parseDate(option.minEstimatedDeliveryDate));
          }
          if (option.maxEstimatedDeliveryDate) {
            dates.push(ShippingEstimate.parseDate(option.maxEstimatedDeliveryDate));
          }
          if (!dates.length) {
            dates.push(...ShippingEstimate.parsePromise(option.deliveryPromise, today));
          }
          dates.sort((a, b) => a - b);
          return {
            title: option.title,
            estimatedCost: option.estimatedCost,
            amount: parseFloat(option.estimatedCost?.amount ?? '0'),
            earliest: dates[0] || null,
            latest: dates[dates.length - 1] || null
          };
        })
        .filter((o) => o.earliest);

      if (!priced.length) return { min: null, max: null, region, option: null, hasFree: false };

      // A truthful claim about availability, unlike naming one rate: it is only
      // ever set from a rate the carriers actually quoted at zero.
      const hasFree = priced.some((o) => o.amount === 0);

      if (mode === 'single') {
        let chosen;
        if (source === 'fastest') {
          chosen = priced.slice().sort((a, b) => a.earliest - b.earliest)[0];
        } else if (source === 'slowest') {
          chosen = priced.slice().sort((a, b) => b.latest - a.latest)[0];
        } else {
          // Cheapest, which is the free rate whenever one exists. Ties break to
          // the sooner date so the buyer is not shown a needlessly late one.
          chosen = priced.slice().sort((a, b) => (
            a.amount - b.amount || a.earliest - b.earliest
          ))[0];
        }
        // The rate's latest date: "arrives by" should not undershoot.
        return { min: chosen.latest, max: null, region, option: chosen, hasFree };
      }

      const earliest = priced.slice().sort((a, b) => a.earliest - b.earliest)[0];
      const latest = priced.slice().sort((a, b) => b.latest - a.latest)[0];

      // No option is returned for a range, so show_method / show_cost render
      // nothing. A range spans several rates — here the ends belong to two
      // different carriers — so naming any one of them would describe a rate that
      // does not own either bound. Only the single-date modes can label a rate
      // truthfully, because there the date and the rate are the same rate.
      return { min: earliest.earliest, max: latest.latest, region, option: null, hasFree };
    }

    /**
     * Reads dates out of a deliveryPromise string.
     *
     * Shopify returns these already localised and without a year — "Tuesday,
     * August 4", sometimes a range. Every date in the string is extracted, so a
     * range contributes both ends.
     *
     * @param {string} text - The deliveryPromise value.
     * @param {Date} today - Today, at UTC midnight.
     * @returns {Array<Date>} Every date found, possibly empty.
     */
    static parsePromise(text, today) {
      if (!text) return [];

      const lower = text.trim().toLowerCase();
      if (lower === 'today') return [today];
      if (lower === 'tomorrow') return [ShippingEstimate.addDays(today, 1, false)];

      const found = [];
      const pattern = /([A-Za-z]{3,})\s+(\d{1,2})(?:\w{2})?(?:,\s*(\d{4}))?/g;
      let match = pattern.exec(text);

      while (match) {
        const month = MONTHS.indexOf(match[1].slice(0, 3).toLowerCase());
        const day = parseInt(match[2], 10);

        if (month !== -1 && day >= 1 && day <= 31) {
          const year = match[3] ? parseInt(match[3], 10) : today.getUTCFullYear();
          let date = new Date(Date.UTC(year, month, day));
          // No year in the string: across a Dec->Jan rollover the nearest
          // sensible reading is next year, not one ten months in the past.
          if (!match[3] && date.getTime() < today.getTime() - 45 * 86400000) {
            date = new Date(Date.UTC(year + 1, month, day));
          }
          found.push(date);
        }
        match = pattern.exec(text);
      }

      return found;
    }

    /**
     * @param {object} payload - The window just rendered.
     */
    writeLastKnown(payload) {
      try {
        localStorage.setItem(LAST_KEY, JSON.stringify({
          at: Date.now(), zip: this.zip, country: this.country, payload
        }));
      } catch (error) {
        // Non-fatal.
      }
    }

    /**
     * Replays the last window this shopper actually saw, when a fresh lookup could
     * not be completed. Only for the same ZIP, and only if the date has not already
     * passed — a stale date in the past is worse than showing nothing.
     *
     * @returns {boolean} Whether a stored window was rendered.
     */
    renderLastKnown() {
      try {
        const stored = JSON.parse(localStorage.getItem(LAST_KEY) || 'null');
        if (!stored?.payload?.min) return false;
        if (stored.zip !== this.zip || stored.country !== this.country) return false;

        const min = new Date(stored.payload.min);
        if (min.getTime() < ShippingEstimate.today().getTime()) return false;

        this.region = stored.payload.region || '';
        this.renderWindow(
          min,
          stored.payload.max ? new Date(stored.payload.max) : null,
          stored.payload.option,
          stored.payload.hasFree
        );
        return true;
      } catch (error) {
        return false;
      }
    }

    readCache() {
      try {
        // localStorage, not sessionStorage: a returning visitor keeps their ZIP in
        // localStorage, so keeping the cache there too means most return visits
        // render instantly instead of firing a throttle-prone call on page load.
        const raw = localStorage.getItem(this.cacheKey);
        if (!raw) return null;
        const { at, payload } = JSON.parse(raw);
        if (Date.now() - at > CACHE_TTL_MS) return null;
        return payload;
      } catch (error) {
        return null;
      }
    }

    writeCache(payload) {
      try {
        localStorage.setItem(this.cacheKey, JSON.stringify({ at: Date.now(), payload }));
      } catch (error) {
        // Non-fatal: we just pay for the request again next time.
      }
    }

    get cacheKey() {
      // Dates roll over at midnight, so the local day is part of the identity.
      const day = new Date().toDateString();
      // cartSignature is always set by resolveLines(); the fallback keeps the key
      // well-formed rather than silently colliding with a differently-scoped entry.
      const province = ShippingEstimate.stateFromZip(this.zip) || '-';
      return `${CACHE_PREFIX}${this.cartSignature || `${this.variantId}:${this.quantity}`}`
        + `|${this.quantity}|${this.country}|${this.zip}|${province}|${day}`;
    }

    /* ------------------------------------------------------------------ *
     * Rendering
     * ------------------------------------------------------------------ */

    renderWindow(min, max, option, hasFree) {
      this.unavailable = false;
      if (this.incompatible) {
        this.incompatible = false;
        this.suppress('incompatible', false);
      }
      this.els.range.textContent = this.formatWindow(min, max);

      if (this.els.method) this.els.method.textContent = option?.title || '';

      const amount = option?.estimatedCost
        ? parseFloat(option.estimatedCost.amount ?? '0')
        : null;

      if (this.els.cost) {
        // Cleared when there is no single rate behind the date, so a previous
        // render's price cannot linger next to a range.
        this.els.cost.textContent = amount === null ? '' : (amount === 0
          ? this.dataset.freeText || 'Free'
          : new Intl.NumberFormat(this.locale, {
            style: 'currency',
            currency: option.estimatedCost.currencyCode
          }).format(amount));
      }

      if (this.els.free) {
        const displayedRateIsFree = amount === 0;

        // Two different things to say, so two different wordings. When the rate on
        // screen is itself free, the shortest true statement is just "Free" — the
        // same word show_cost would use. Only when the buyer is looking at a paid
        // rate is there a separate fact to announce: a free one exists elsewhere.
        this.els.free.textContent = displayedRateIsFree
          ? this.dataset.freeText || 'Free'
          : this.dataset.freeShippingText || 'Free shipping available';

        // show_cost already prints "Free" for that same rate, so avoid saying it
        // twice when both settings are on.
        const costAlreadySaysFree = !!this.els.cost && displayedRateIsFree;
        this.els.free.hidden = !hasFree || costAlreadySaysFree;
      }

      this.paintDestination();
      this.setState('ready');
    }

    /**
     * Shimmer state. The date text is transparent while loading, so when there is
     * nothing to shimmer over yet a blank placeholder gives the bar its width.
     */
    showLoading() {
      if (this.els.range && !this.els.range.textContent.trim()) {
        this.els.range.textContent = ' '.repeat(20);
      }
      this.paintDestination();
      this.setState('loading');
    }

    /**
     * What to show before a ZIP is known.
     *
     * Defaults to asking for one. Carriers will not quote without a ZIP and
     * Shopify's IP geo resolves country only, so there is no real date to show
     * yet — and an invented one would contradict checkout.
     */
    renderColdStart() {
      if (this.coldStart === 'prompt' && this.els.prompt) {
        this.paintDestination();
        this.setState('prompt');
        return;
      }
      this.renderGenericWindow();
    }

    /**
     * The generic day range, with no rate behind it.
     */
    renderGenericWindow() {
      const today = ShippingEstimate.today();
      // Always a range, even when the block is set to show a single date. This
      // window is a guess, not a carrier quote — collapsing it to one date would
      // present a promise the store cannot stand behind. Only a real rate earns a
      // single date.
      this.els.range.textContent = this.formatWindow(
        ShippingEstimate.addDays(today, this.fallbackMin, this.skipWeekends),
        ShippingEstimate.addDays(today, this.fallbackMax, this.skipWeekends)
      );

      if (this.els.method) this.els.method.textContent = '';
      if (this.els.cost) this.els.cost.textContent = '';
      // No rates were fetched, so nothing is known about free shipping.
      if (this.els.free) this.els.free.hidden = true;

      this.paintDestination();
      this.setState('ready');
    }

    /**
     * Shopify throttled the carrier lookup, so no real date is available for a ZIP
     * that may be perfectly valid. Falls back to the generic range and labels the
     * destination as the country rather than the ZIP's state — claiming to have
     * priced a specific address we could not reach would be misleading.
     */
    /**
     * @returns {boolean} Whether the block currently occupies space on the page.
     *   False for the A/B control, which is display:none, and for any suppressed
     *   state. True once a variation reveals it.
     */
    isPainted() {
      return this.getBoundingClientRect().height > 0;
    }

    /**
     * @param {string|number} variantId - Variant to test.
     * @returns {boolean} True when it is buyable but not physically in stock, and
     *   the block is configured to hide in that case.
     */
    isBackordered(variantId) {
      if (this.showOnBackorder) return false;
      return this.backorderIds.has(String(variantId));
    }

    /**
     * Hides the block for a stated reason, and keeps hiding it while any reason
     * stands. Inline !important on purpose: the A/B variation reveals the widget
     * with `display: block !important`, which would otherwise win and show a
     * delivery date for something the buyer cannot actually order.
     *
     * @param {string} reason - Why it is hidden, e.g. 'out-of-stock'.
     * @param {boolean} on - Whether the reason applies.
     */
    suppress(reason, on) {
      this.suppressReasons = this.suppressReasons || new Set();
      if (on) this.suppressReasons.add(reason);
      else this.suppressReasons.delete(reason);

      this.classList.toggle(`shipping-estimate--${reason}`, on);

      if (this.suppressReasons.size) {
        this.style.setProperty('display', 'none', 'important');
      } else {
        this.style.removeProperty('display');
      }
    }

    renderDisabled() {
      console.warn( // eslint-disable-line no-console
        `[shipping-estimate] delivery date fields are missing from API version `
        + `"${this.apiVersion}" — hiding the widget. Check whether Shopify has `
        + `moved deliveryPromise / min-maxEstimatedDeliveryDate on CartDeliveryOption.`
      );
      this.incompatible = true;
      this.setState('idle');
      this.suppress('incompatible', true);
    }

    renderUnavailable() {
      // Logged so throttling can be tracked in the field; it is invisible to the
      // buyer, who just sees the generic range.
      console.warn( // eslint-disable-line no-console
        `[shipping-estimate] ${this.failureReason || 'carrier rates unavailable'} `
        + `— falling back to the generic `
        + `${this.fallbackMin}-${this.fallbackMax} day window and showing `
        + `"${this.countryName}" as the destination (zip ${this.zip || 'none'})`
      );
      // Reached only after every retry was throttled. Prefer replaying the real date
      // this shopper was last quoted: it is exact, needs no correction afterwards,
      // and avoids the generic range the client objected to.
      if (this.renderLastKnown()) return;

      this.unavailable = true;
      this.region = '';
      this.renderGenericWindow();
    }

    /**
     * Shows the state Shopify rated the address for once a ZIP resolves
     * ("California"). Falls back to the raw ZIP when there is no token to resolve
     * it with, and to the country before any ZIP is entered.
     */
    paintDestination() {
      if (!this.els.destinationText) return;
      if (this.unavailable) {
        this.els.destinationText.textContent = this.countryName;
        return;
      }
      const region = this.region || ShippingEstimate.stateFromZip(this.zip);
      this.els.destinationText.textContent = region || this.zip || this.countryName;
    }

    /**
     * @param {string} zip - US ZIP code.
     * @returns {string} Full state name, or '' if the prefix is unassigned.
     */
    static stateFromZip(zip) {
      const prefix = parseInt(String(zip || '').trim().slice(0, 3), 10);
      if (Number.isNaN(prefix)) return '';
      const match = ZIP_RANGES.find(([low, high]) => prefix >= low && prefix <= high);
      return match ? STATE_NAMES[match[2]] || '' : '';
    }

    /* ------------------------------------------------------------------ *
     * Dates
     * ------------------------------------------------------------------ */

    /**
     * Today's calendar date in the buyer's own timezone, pinned to UTC midnight so
     * it survives UTC formatting unchanged.
     * @returns {Date} Start of today.
     */
    static today() {
      const now = new Date();
      return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    }

    /**
     * @param {string} value - ISO8601 date or date-time from the Storefront API.
     * @returns {Date} Parsed date.
     */
    static parseDate(value) {
      return new Date(/T/.test(value) ? value : `${value}T00:00:00Z`);
    }

    /**
     * @param {Date} from - Start date.
     * @param {number} days - Days to add.
     * @param {boolean} skipWeekends - Count business days only, skipping Sat/Sun.
     * @returns {Date} Resulting date.
     */
    static addDays(from, days, skipWeekends) {
      const date = new Date(from.getTime());

      if (!skipWeekends) {
        date.setUTCDate(date.getUTCDate() + days);
        return date;
      }

      let added = 0;
      while (added < days) {
        date.setUTCDate(date.getUTCDate() + 1);
        const day = date.getUTCDay();
        if (day !== 0 && day !== 6) added += 1;
      }
      return date;
    }

    /**
     * Formats a delivery window the way the checkout does.
     *
     * Dates are formatted in UTC on purpose. Formatting in the browser's zone
     * shifts the day backwards for anyone west of UTC and would show a date the
     * checkout never displays.
     *
     * Both ends are written out in full ("Tue, Aug 4 - Wed, Aug 5") rather than
     * collapsed via Intl.formatRange, which would render "Tue, Aug 4 - Wed, 5".
     *
     * @param {Date} min - Earliest delivery date.
     * @param {Date|null} max - Latest delivery date.
     * @returns {string} Formatted delivery window.
     */
    formatWindow(min, max) {
      const formatter = new Intl.DateTimeFormat(this.locale, {
        timeZone: 'UTC',
        weekday: this.dataset.showWeekday === 'false' ? undefined : 'short',
        month: 'short',
        day: 'numeric'
      });

      if (!max || min.getTime() === max.getTime()) return formatter.format(min);
      return `${formatter.format(min)}${this.separator}${formatter.format(max)}`;
    }

    /**
     * 'loading' keeps the current text in place and lets CSS shimmer over it, so
     * the card never collapses or jumps height while rates are being fetched.
     *
     * @param {string} state - 'idle', 'prompt', 'loading' or 'ready'.
     */
    setState(state) {
      this.dataset.state = state;
      const showDates = state === 'ready' || state === 'loading';
      if (this.els.result) this.els.result.hidden = !showDates;
      if (this.els.prompt) this.els.prompt.hidden = state !== 'prompt';
      if (this.els.destination) this.els.destination.hidden = state === 'idle';
    }
  }

  customElements.define('shipping-estimate', ShippingEstimate);
}
